/**
 * AttachmentCard — a file shared in a conversation (chat, team channel, meeting
 * chat, guest chat). Images show inline (fetched with the viewer's token, since
 * file downloads need it); other files show name and size with Download, and
 * Edit for Office documents, which opens the Collabora editor right here.
 *
 * Guests pass their session token and canEdit={false}: they may view and
 * download what's shared in their meeting, never edit it.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, FileText, Pencil, Loader2 } from 'lucide-react';
import { API_BASE } from '../../lib/api';
import { isImageAttachment, type AttachmentMeta } from '../../lib/attachments';
import { isEditableDocument } from '../../lib/officeEditor';
import { OfficeEditor } from './OfficeEditor';
import { LicenseLockIcon, LOCKED_CLASS, useLicenseGate } from '../ui/LicenseLock';

interface AttachmentCardProps {
  file: AttachmentMeta;
  /** Bearer token for downloads; defaults to the signed-in user's */
  token?: string;
  canEdit?: boolean;
  compact?: boolean;
  /** Overrides the API base (the guest page has its own) */
  apiBase?: string;
}

const userToken = () => localStorage.getItem('eurion_token') ?? '';

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

async function fetchBlob(apiBase: string, fileId: string, token: string): Promise<Blob> {
  const res = await fetch(`${apiBase}/v1/files/${encodeURIComponent(fileId)}/download`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(String(res.status));
  return res.blob();
}

export function AttachmentCard({ file, token, canEdit = true, compact = false, apiBase = API_BASE }: AttachmentCardProps) {
  const { t } = useTranslation();
  // Editing in Office needs the license's office feature; downloading never does
  const office = useLicenseGate('office');
  const image = isImageAttachment(file);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageFailed, setImageFailed] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadFailed, setDownloadFailed] = useState(false);
  const [editing, setEditing] = useState(false);

  // Only images are fetched up front; other files download on click
  useEffect(() => {
    if (!image) return;
    let url: string | null = null;
    let cancelled = false;
    fetchBlob(apiBase, file.fileId, token ?? userToken())
      .then((blob) => {
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setImageUrl(url);
      })
      .catch(() => { if (!cancelled) setImageFailed(true); });
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [image, file.fileId, token, apiBase]);

  const download = async () => {
    setDownloading(true);
    setDownloadFailed(false);
    try {
      const href = URL.createObjectURL(await fetchBlob(apiBase, file.fileId, token ?? userToken()));
      const a = document.createElement('a');
      a.href = href;
      a.download = file.filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(href), 10_000);
    } catch {
      setDownloadFailed(true);
    }
    setDownloading(false);
  };

  if (image && imageUrl && !imageFailed) {
    return (
      <figure className={`mt-1 ${compact ? 'max-w-[14rem]' : 'max-w-sm'}`}>
        <button onClick={() => void download()} className="block cursor-zoom-in" aria-label={`${t('files.download')} ${file.filename}`}>
          <img
            src={imageUrl}
            alt={file.filename}
            onError={() => setImageFailed(true)}
            className={`w-auto max-w-full rounded-lg border border-nord-3/40 object-contain shadow-sm ${compact ? 'max-h-48' : 'max-h-80'}`}
          />
        </button>
        <figcaption className="mt-1 truncate text-2xs text-nord-muted">{file.filename}</figcaption>
      </figure>
    );
  }

  const editable = canEdit && isEditableDocument(file.filename) && !office.hidden;
  return (
    <>
      <div className={`mt-1 flex items-center gap-2.5 rounded-lg border border-nord-3/40 bg-nord-1 px-3 py-2 ${compact ? 'max-w-[16rem]' : 'max-w-md'}`}>
        {image && !imageUrl && !imageFailed
          ? <Loader2 className="h-5 w-5 shrink-0 animate-spin text-nord-muted" aria-hidden="true" />
          : <FileText className="h-5 w-5 shrink-0 text-nord-8" aria-hidden="true" />}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-nord-6">{file.filename}</p>
          {downloadFailed
            ? <p role="alert" className="text-2xs text-nord-11">{t('channel.downloadFailed')}</p>
            : file.size !== undefined && <p className="text-2xs text-nord-muted">{formatBytes(file.size)}</p>}
        </div>
        {editable && (
          <button
            onClick={office.guard(() => setEditing(true))}
            className={`flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-frost-1 transition-colors hover:bg-frost-1/10 ${office.locked ? LOCKED_CLASS : ''}`}
            aria-label={t('office.editNamed', { name: file.filename })}
            {...office.attrs}
          >
            {office.locked ? <LicenseLockIcon /> : <Pencil className="h-3.5 w-3.5" aria-hidden="true" />}
            {!compact && t('office.edit')}
          </button>
        )}
        <button
          onClick={() => void download()}
          disabled={downloading}
          className="rounded-md p-1.5 text-nord-4 transition-colors hover:bg-nord-2 hover:text-nord-6 disabled:opacity-50"
          title={t('files.download')}
          aria-label={`${t('files.download')} ${file.filename}`}
        >
          {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
        </button>
      </div>
      {editing && <OfficeEditor fileId={file.fileId} fileName={file.filename} onClose={() => setEditing(false)} />}
    </>
  );
}
